<template>
    <div class="NodeEditor ProcessNodeEditor">
        <NodeSaveStatus></NodeSaveStatus>
        <!--Header Editor-->
        <NodeHeaderEditor
            key="NodeHeaderEditor"
            :name.sync="node.name"
            :id="node.id"
            :slug="node.slug"
            :type="node.type"
            ref="NodeHeaderEditor"
            @save="saveNode"
            @delete="deleteNode"
            @close="closePanelRight"
        ></NodeHeaderEditor>
        <!--Content Editor-->
        <v-tabs slider-color="#FFFFFF" v-model="currentTab">
            <v-tab :href="`#tab-prompt`">Prompt</v-tab>
            <v-tab :href="`#tab-setting`" v-if="node.config.nlpConfig || node.config.setting">{{
                    $f.tc('setting')
                }}
            </v-tab>
            <v-tab v-show="node.config.cardList.length > 0" :href="`#tab-action`">{{ $f.tc('action') }}</v-tab>
            <v-tabs-items touchless v-model="currentTab">
                <v-tab-item :value="`tab-prompt`">
                    <div class="scroll-container fci-scrollbar">
                        <ScenarioCard name="Prompt" key="Prompt" class="custom-card">
                            <PromptList
                                v-model="prompts"
                                :fallback="fallback"
                                :node-audio-list="scenarioAudioList"
                                :audio-slug="node.config['audio_slug']"
                                :llm-response="node.config['llmResponse']"
                                :llm-response-list="llmResponses"
                                @savePrompts="updatePrompts"
                                @saveAudio="updateAudio"
                                @updateFallback="updateFallback"
                                @saveResponse="updateLLMResponse"
                            ></PromptList>
                        </ScenarioCard>
                        <!-- Reprompt -->
                        <ScenarioCard name="RePrompt" key="RePrompt">
                            <RePromptEditor
                                :items.sync="node.config.rePromptList"
                                @saveReprompt="shouldSave ? updateRePrompts() : null"
                                :node-audio-list="scenarioAudioList"
                                :llm-response-list="llmResponses"
                            ></RePromptEditor>
                        </ScenarioCard>
                    </div>
                </v-tab-item>
                <v-tab-item :value="`tab-setting`">
                    <!-- Section: NLP setting -->
                    <div class="scroll-container fci-scrollbar">
                        <ScenarioCard
                            v-if="node.config.nlpConfig && scenarioSetting.version > 1"
                            :name="$f.tc('treeNode.setting.nlpSetting')"
                            key="nlp-setting"
                            ref="NLPConfiguration"
                        >
                            <v-checkbox
                                hide-details
                                v-model="node.config.nlpConfig.dontUseNlp"
                                color="#0052CC"
                                :label="this.$t('treeNode.setting.dontUseNlp')"
                                @change="saveNLPConfig"
                                :readonly="!isEdit"
                            ></v-checkbox>
                            <div v-if="!node.config.nlpConfig.dontUseNlp" class="mt-12">
                                <v-select
                                    hide-details outlined
                                    v-model="node.config.nlpConfig.label"
                                    :items="nlpConfigs.filter(i => i.label !== 'tenant_name')"
                                    @input="saveNLPConfig"
                                    class="input-custom"
                                    item-text="label"
                                    item-value="label"
                                    :readonly="!isEdit"
                                ></v-select>
                            </div>
                        </ScenarioCard>
                        <ScenarioCard
                            v-if="node.config.llmClassifier && node.config.llmClassifier.length > 0 && scenarioSetting.version > 1"
                            :name="$f.tc('treeNode.setting.llmClassifier')"
                            key="llm-classifier"
                            ref="LLMClassifier"
                            :delete-able="isEdit"
                            @delete="deleteSetting('llm-classifier')"
                        >
                            <div v-for="(item, index) in node.config.llmClassifier" :key="index"
                                 class="response-config mb-2">
                                <div style="position: relative">
                                    <div v-if="node.config.llmClassifier.length > 1" class="classifier-header mb-2">
                                        <div v-if="isEdit" style="position: absolute; top:0; right:0">
                                            <div class="btn-icon-2 ml-6" v-if="isEdit">
                                                <img
                                                    src="/img/scenario/delete-new_red.svg"
                                                    alt="icon-delete"
                                                    style="width: 18px;"
                                                    @click="deleteClassifier(index)"
                                                >
                                            </div>
                                        </div>
                                    </div>
                                    <div class="label">{{ $f.tc('llmClassifier') }}</div>
                                    <v-select
                                        hide-details outlined
                                        v-model="item.label"
                                        :items="llmClassifiers"
                                        class="input-custom mb-2"
                                        :readonly="!isEdit"
                                        @change="saveClassifierConfig"
                                    ></v-select>
                                    <div class="label">{{ $f.tc('setResultToVariable') }}</div>
                                    <v-text-field
                                        hide-details outlined
                                        v-model="item.assignVariable"
                                        class="input-custom"
                                        :readonly="!isEdit"
                                        @blur="saveClassifierConfig"
                                    ></v-text-field>
                                </div>
                            </div>
                            <div v-if="isEdit" class="centerY justify-start mt-6">
                                <div class="btn-outline" @click="addClassifier">
                                    <img src="/img/add-blue.svg" alt="icon-add-interrupt-config"
                                         style="margin-right: 6px; transform: scale(1.1) ">
                                    {{ $f.tc('addNew') }}
                                </div>
                            </div>
                        </ScenarioCard>
                        <ScenarioCard
                            v-if="node.config.nlpConfig && scenarioSetting.version > 1 && isShowUseKb()"
                            name="KB Setting"
                            key="kb-setting"
                            ref="KBSetting"
                        >
                            <v-checkbox
                                hide-details
                                v-model="node.config['use_kb']"
                                color="#0052CC"
                                :label="$f.tc('useKb')"
                                @change="saveNode"
                                :readonly="!isEdit"
                            ></v-checkbox>
                        </ScenarioCard>
                        <ScenarioCard
                            name="Setting"
                            key="Setting"
                            v-if="showSetting"
                            ref="Setting"
                            :delete-able="isEdit"
                            @delete="deleteSetting('setting')"
                        >
                            <SettingEditor
                                :items.sync="node.config.setting"
                                :editable="isEdit"
                                :node-setting-variable-list="nodeSettingVariable"
                                :invalidKey="['audio_engine', 'voicedna']"
                                :keysIgnored="['audio_engine', 'voicedna']"
                                @save="shouldSave ? saveNode() : null"
                            ></SettingEditor>
                        </ScenarioCard>
                        <ScenarioCard
                            name="Audio Engine Setting"
                            key="audio-engine-setting"
                            v-if="showAudioEngine"
                            ref="AudioEngineSetting"
                            :delete-able="isEdit"
                            @delete="deleteSetting('audio-engine')"
                        >
                            <AudioEngineEditor
                                v-model="node.config.setting"
                                :editable="isEdit"
                                :nodeID="node.id"
                                @save="shouldSave ? saveNode() : null"
                            ></AudioEngineEditor>
                        </ScenarioCard>
                        <ScenarioCard
                            name="Pre Cached TTS Setting"
                            key="pre-cache-tts-setting"
                            ref="PreCachedTTSSetting"
                        >
                            <v-checkbox
                                hide-details
                                v-model="node.config['skipPreTTS']"
                                color="#0052CC"
                                :label="$f.tc('skipTTSPreCached')"
                                @change="saveNode"
                                :readonly="!isEdit"
                            ></v-checkbox>
                        </ScenarioCard>
                    </div>
                </v-tab-item>
                <v-tab-item :value="`tab-action`" v-show="node.config.cardList.length > 0">
                    <div class="scroll-container fci-scrollbar">
                        <template v-for="(card, cardIndex) in node.config.cardList">
                            <ScenarioCard
                                :key="cardIndex"
                                v-if="isShowCard(card)"
                                :name.sync="card.name"
                                @delete="deleteCard(cardIndex)"
                                @moveUp="moveCardUp(cardIndex)"
                                @moveDown="moveCardDown(cardIndex)"
                                @updateCardName="newName => updateCardName(newName, cardIndex)"
                                :delete-able="isEdit"
                                :move-able="isEdit"
                                :name-editable="isEdit"
                                :name-invalid="['init', 'end']"
                            >
                                <ApiEditor
                                    v-if="card.type === CardType.API"
                                    :item.sync="card.data"
                                    @save="shouldSave ? saveNode() : null"
                                    ref="card"
                                    :editable="isEdit"
                                ></ApiEditor>
                                <ApiParameterEditor
                                    v-if="card.type === CardType.SET_USER_ATTRIBUTE"
                                    :items.sync="card.data"
                                    @save="shouldSave ? saveNode() : null"
                                    ref="card"
                                    :editable="isEdit"
                                ></ApiParameterEditor>
                                <LuaEditor
                                    v-if="card.type === CardType.LUA"
                                    :item.sync="card.data"
                                    @save="shouldSave ? saveNode() : null"
                                    ref="card"
                                    :editable="isEdit"
                                ></LuaEditor>
                            </ScenarioCard>
                        </template>
                    </div>
                </v-tab-item>
            </v-tabs-items>
        </v-tabs>
        <!--Footer Editor-->
        <NodeFooterEditor
            class="NodeFooterEditor"
            @menuSelect="onMenuSelect($event)"
        ></NodeFooterEditor>
    </div>
</template>

<script>
import NodeEditor from "@/views/Scenario/NodeEditor/NodeEditor";
import ApiEditor from "@/views/Scenario/NodeEditor/common/ApiEditor";
import ApiParameterEditor from "@/views/Scenario/NodeEditor/common/ApiParameterEditor";
import ScenarioCard from "@/views/Scenario/components/ScenarioCard"
import RePromptEditor from "@/views/Scenario/NodeEditor/process/RePromptEditor";
import NodeHeaderEditor from "@/views/Scenario/NodeEditor/common/NodeHeaderEditor";
import NodeSaveStatus from "@/views/Scenario/NodeEditor/NodeSaveStatus";
import NodeFooterEditor from "@/views/Scenario/NodeEditor/common/NodeFooterEditor";
import LuaEditor from "@/views/Scenario/NodeEditor/common/LuaEditor";
import {mapState} from 'vuex';
import UserService from "@/services/user.service";
import SettingEditor from "@/views/Scenario/NodeEditor/common/SettingEditor";
import ScenarioService from "@/services/scenario.service";
import PromptList from "@/views/Scenario/NodeEditor/process/PromptList";
import AudioEngineEditor from "@/views/Scenario/NodeEditor/common/AudioEngineEditor";

const NLP_SETTING_OPTIONS = {
    DONT_USE_NLP: 'DONT_USE_NLP',
    INHERIT_PARENT_NODE: 'INHERIT_PARENT_NODE',
    OTHER: 'OTHER'
}

const {CardType} = ScenarioService
const defaultPrompts = [
    {
        content: "",
        tag: "prompt_1"
    }
]
const defaultReprompts = [
    {
        content: "",
        tag: "reprompt_1"
    }
]

const defaultClassifierConfig = {
    label: "",
    assignVariable: ""
}

export default {
    name: "ProcessNodeEditor",
    extends: NodeEditor,
    components: {
        AudioEngineEditor,
        PromptList,
        LuaEditor,
        NodeFooterEditor,
        ApiParameterEditor,
        RePromptEditor,
        ApiEditor,
        ScenarioCard,
        NodeHeaderEditor,
        NodeSaveStatus,
        SettingEditor
    },
    data() {
        return {
            NLP_SETTING_OPTIONS,
            prompts: this.$utils.cloneObject(defaultPrompts),
            nodeAudioList: [],
            cardSettings: {},
            fallback: {
                content: "",
                tag: "Fallback"
            },
        }
    },
    computed: {
        ...mapState({
            scenario: state => state.scenario.scenario,
            nlpList: state => state.campaign.bots,
            nodeSettingVariable: state => state.scenario.nodeSettingVariableList,
            scenarioSetting: state => state.scenario.scenario_setting,
            parentMap: state => state.scenario.parentMap,
            scenarioAudioList: state => state.scenario.scenarioAudioList,
            kbSettingList: state => state.scenario.kbSettingList,
            audioEngineList: state => state.scenario.audioEngineList,
            treeData: state => state.scenario.treeData,
            useGuardrail: state => state.scenario.useGuardrail,
            llmConfigs: state => state.scenario.llmConfigs
        }),
        nlpConfigs() {
            let nlpSetting = []
            if (this.scenarioSetting) {
                nlpSetting = this.scenarioSetting.nlp_setting ? this.scenarioSetting.nlp_setting : []
            }
            if (nlpSetting.length === 0) {
                return []
            } else {
                let configs = []
                let labels = Object.keys(nlpSetting)
                labels.forEach(i => {
                    configs.push({
                        label: i,
                        code: nlpSetting[i]['code'],
                        name: nlpSetting[i]['name']
                    })
                })
                return configs
            }
        },
        llmClassifiers() {
            let classifierList = []
            if (this.llmConfigs) {
                classifierList = this.llmConfigs.filter(i => i.type === 'llm_classifier')
            }
            if (classifierList.length === 0) {
                return []
            } else {
                let configs = []
                classifierList.forEach(item => {
                    configs.push(item.label)
                })
                return configs
            }
        },
        llmResponses() {
            let responseList = []
            if (this.llmConfigs) {
                responseList = this.llmConfigs.filter(i => i.type === 'llm_response')
            }
            if (responseList.length === 0) {
                return []
            } else {
                let configs = []
                responseList.forEach(item => {
                    configs.push(item.label)
                })
                return configs
            }
        }
    },
    created() {
        window.onbeforeunload = function () {
            UserService.getProfile()
        }
    },
    mounted() {
        this.initPrompts()
        this.initRePrompts()
    },
    methods: {
        validateNodeData() {
            if (!this.node.name) {
                return 'Name not valid'
            }
            let config = this.node.config
            if (config.rePromptList) {
                for (let rePrompt of config.rePromptList) {
                    if (!rePrompt.name) {
                        return 'RePrompt name not valid'
                    }
                    if (!rePrompt.prompt) {
                        return 'RePrompt content not valid'
                    }
                    let validateConditionList = this.validateConditionList(rePrompt.conditionList)
                    if (validateConditionList !== true) {
                        return validateConditionList
                    }
                }
            }
            return true
        },
        // Functions handle for Prompts
        initPrompts() {
            let prompt = this.node.config.prompt
            let prompts = this.node.config.prompts
            this.prompts = prompts
            this.fallback = this.node.config.promptFallback
            if (!prompts || prompts.length === 0) {
                let promptList = prompt ? prompt.split("||").map(i => i.trim()) : [""]
                let arrPrompts = []
                promptList.forEach((prompt, index) => {
                    arrPrompts.push({
                        content: prompt,
                        tag: `prompt_${index + 1}`
                    })
                })
                this.prompts = arrPrompts.length > 0 ? arrPrompts : this.$utils.cloneObject(defaultPrompts)
            }
        },
        initRePrompts() {
            if (!this.node.config.rePromptList) {
                return
            }
            this.node.config.rePromptList.forEach(item => {
                let reprompt = item.prompt
                let reprompts = item.prompts
                if (!reprompts || reprompts.length === 0) {
                    let repromptList = reprompt ? reprompt.split("||").map(i => i.trim()) : [""]
                    let arrPrompts = []
                    repromptList.forEach((prompt, index) => {
                        arrPrompts.push({
                            content: prompt,
                            tag: `${item.name}_${index + 1}`
                        })
                    })
                    item.prompts = arrPrompts.length > 0 ? arrPrompts : this.$utils.cloneObject(defaultReprompts)
                }
            })
        },
        updateAudio(value) {
            this.node.config['audio_slug'] = value
            this.saveNode()
        },
        updateRePrompts() {
            this.node.config.rePromptList.forEach(item => {
                if (item.prompts) {
                    item.prompt = item.prompts.reduce((result, i) => result + `${result !== "" ? '||' : ''}${i.content}`, "")
                }
            })
            this.saveNode()
        },
        updatePrompts() {
            this.node.config.prompts = this.prompts
            this.node.config.prompt = this.prompts.reduce((result, item) => result + `${result !== "" ? '||' : ''}${item.content}`, "")
            this.saveNode()
        },
        updateFallback(promptFallback) {
            this.node.config.promptFallback = promptFallback
            this.saveNode()
        },
        saveNLPConfig() {
            let nlpConfig = this.node.config.nlpConfig
            let label = nlpConfig.label ? nlpConfig.label : this.nlpConfigs[0].label
            nlpConfig.label = nlpConfig.dontUseNlp ? '' : label
            if (this.shouldSave) {
                this.saveNode()
            }
        },
        isShowUseKb() {
            if (this.kbSettingList[0]) {
                return this.kbSettingList[0]['meta_data']['intents'].length > 0
            }

            return false
        },
        isShowCard(card) {
            if (!this.node) return false

            if (this.node.id === this.treeData.startId) {
                return (card.name !== 'init' && card.name !== 'end') || card.type !== CardType.LUA
            }

            return true
        },
        deleteSetting(nameField = '') {
            this.$appService.confirm(this.$t('confirmDelete', {item: this.$t(nameField)})).then(
                (confirm) => {
                    if (!confirm) {
                        return
                    }

                    switch (nameField) {
                        case 'setting':
                            Object.keys(this.node.config.setting).forEach((key) => {
                                if (key !== 'audio_engine' && key !== 'voicedna') {
                                    delete this.node.config.setting[key]
                                }
                            })
                            if (Object.keys(this.node.config.setting).length === 0) {
                                delete this.node.config.setting
                            }
                            this.showSetting = false
                            this.saveNode()
                            break
                        case 'audio-engine':
                            delete this.node.config.setting['audio_engine']
                            delete this.node.config.setting['voicedna']
                            this.showAudioEngine = false
                            this.saveNode()
                            break
                        case 'llm-classifier':
                            this.node.config.llmClassifier = []
                            this.saveNode()
                            break
                        default:
                            return
                    }
                }
            )
        },
        deleteClassifier(index) {
            this.node.config.llmClassifier.splice(index, 1)
            this.saveClassifierConfig()
        },
        addClassifier() {
            let newConfig = this.$utils.cloneObject(defaultClassifierConfig)
            newConfig.label = this.llmClassifiers[0]
            newConfig.assignVariable = this.$filters.replaceSpecialCharactersUnderscore(this.$utils.nonAccentVietnamese(this.llmClassifiers[0]))
            this.node.config.llmClassifier.push(newConfig)
            this.saveClassifierConfig()
        },
        saveClassifierConfig() {
            let classifierConfig = this.node.config.llmClassifier
            classifierConfig.forEach(item => {
                if (!item.label || !this.llmClassifiers.includes(item.label)) {
                    item.label = this.llmClassifiers[0]
                }
                if (!item.assignVariable) {
                    item.assignVariable = this.$filters.replaceSpecialCharactersUnderscore(this.$utils.nonAccentVietnamese(this.llmClassifiers[0]))
                }
            })
            if (this.shouldSave) {
                this.saveNode()
            }
        },
        updateLLMResponse(value) {
            this.node.config['llmResponse'] = value
            this.saveNode()
        },
    },
    watch: {
        node: {
            deep: true,
            handler: function () {
                this.initPrompts()
                this.initRePrompts()
            }
        },
    }
}
</script>
<style scoped lang="scss">
@import "src/views/Scenario/NodeEditor/NodeEditor";

.ProcessNodeEditor {
    ::v-deep .v-window__container {
        height: calc(100% - 96px) !important;
    }
}

.input-custom {
    ::v-deep {
        .v-input__slot {
            height: 32px;
            background: #FFFFFF;
            border-radius: 5px;
            border: 1px solid #D9D9D9;
            outline: none;
            font-size: 14px;
            padding: 0 6px 0 12px;
            margin: 0;
            display: flex;
            align-items: center;

            &:before, &:after {
                border: none !important;
            }
        }
    }
}

.custom-card {
    ::v-deep .ScenarioCardBody {
        padding: 0 !important;
    }
}

::v-deep {
    .v-input--selection-controls {
        padding-top: 0 !important;
    }

    .v-input--selection-controls__ripple {
        display: none;
    }

    .v-text-field {
        padding-top: 0 !important;
        margin-top: 0 !important;
    }

    .v-input--selection-controls.v-input .v-label {
        font-family: "open sans", serif !important;
        font-size: 14px !important;
        color: #2c3e50 !important;
    }
}

.response-config {
    border-radius: 5px;
    border: 1px solid #D9D9D9;
    padding: 10px 12px;
}

.classifier-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}
</style>
